import { ref, Ref } from 'vue';
import { UserState } from '@/composables/User';
import { GetWhatsappValidationStatus, SubmitWhatsappValidation } from '@/composables/services/Whatsapp';

/**
 * WhatsApp template validation status enum
 */
export enum WhatsappValidationStatus {
  NOT_SUBMITTED = 'NOT_SUBMITTED',
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  IN_APPEAL = 'IN_APPEAL',
  PAUSED = 'PAUSED',
  DISABLED = 'DISABLED',
  FLAGGED = 'FLAGGED',
  ARCHIVED = 'ARCHIVED',
  DELETED = 'DELETED',
  PENDING_DELETION = 'PENDING_DELETION',
  LIMIT_EXCEEDED = 'LIMIT_EXCEEDED',
  LOCKED = 'LOCKED',
  REINSTATED = 'REINSTATED',
  ERROR = 'ERROR',
}

/**
 * Check if a status is still pending (not final, may still change)
 */
export function isPendingStatus(status: string): boolean {
  return status === WhatsappValidationStatus.PENDING
    || status === WhatsappValidationStatus.IN_APPEAL;
}

/**
 * Check if a status is blocking (prevents operator from being enabled)
 */
export function isBlockingStatus(status: string): boolean {
  return status !== WhatsappValidationStatus.APPROVED
    && status !== WhatsappValidationStatus.REINSTATED;
}

/**
 * Get warning severity for a validation status
 * - PENDING: warning (orange)
 * - REJECTED/ERROR: error (red)
 */
export function getStatusSeverity(status: string): 'warning' | 'error' | null {
  switch (status as WhatsappValidationStatus) {
    case WhatsappValidationStatus.PENDING:
    case WhatsappValidationStatus.IN_APPEAL:
    case WhatsappValidationStatus.FLAGGED:
    case WhatsappValidationStatus.LOCKED:
      return 'warning';
    case WhatsappValidationStatus.REJECTED:
    case WhatsappValidationStatus.PAUSED:
    case WhatsappValidationStatus.DISABLED:
    case WhatsappValidationStatus.DELETED:
    case WhatsappValidationStatus.PENDING_DELETION:
    case WhatsappValidationStatus.ARCHIVED:
    case WhatsappValidationStatus.LIMIT_EXCEEDED:
    case WhatsappValidationStatus.ERROR:
      return 'error';
    default:
      return null;
  }
}

/**
 * Get i18n message key for a validation status
 * @param isBulk - If true, use bulk-specific messages (without "operator cannot be enabled" part)
 */
export function getStatusMessageKey(status: string, isBulk = false): string {
  const suffix = isBulk ? 'Bulk' : '';
  switch (status as WhatsappValidationStatus) {
    case WhatsappValidationStatus.PENDING:
      return `whatsappValidation.warnings.pending${suffix}`;
    case WhatsappValidationStatus.REJECTED:
      return `whatsappValidation.warnings.rejected${suffix}`;
    case WhatsappValidationStatus.IN_APPEAL:
      return `whatsappValidation.warnings.inAppeal${suffix}`;
    case WhatsappValidationStatus.PAUSED:
      return `whatsappValidation.warnings.paused${suffix}`;
    case WhatsappValidationStatus.DISABLED:
      return `whatsappValidation.warnings.disabled${suffix}`;
    case WhatsappValidationStatus.FLAGGED:
      return `whatsappValidation.warnings.flagged${suffix}`;
    case WhatsappValidationStatus.ARCHIVED:
      return `whatsappValidation.warnings.archived${suffix}`;
    case WhatsappValidationStatus.DELETED:
    case WhatsappValidationStatus.PENDING_DELETION:
      return `whatsappValidation.warnings.deleted${suffix}`;
    case WhatsappValidationStatus.LIMIT_EXCEEDED:
      return `whatsappValidation.warnings.limitExceeded${suffix}`;
    case WhatsappValidationStatus.LOCKED:
      return `whatsappValidation.warnings.locked${suffix}`;
    case WhatsappValidationStatus.ERROR:
      return `whatsappValidation.warnings.unknown${suffix}`;
    default:
      return '';
  }
}

export interface WhatsappValidationResult {
  success: boolean;
  status: WhatsappValidationStatus;
  error?: string;
}

/**
 * Composable for WhatsApp template validation
 * Handles submission, status checking, and polling
 */
export function useWhatsappTemplateValidation() {
  const isSubmitting = ref(false);
  const isChecking = ref(false);
  const pollingIntervalId = ref<number | null>(null);

  /**
   * Get the shop ID from the active shop
   */
  const getShopId = (): number => {
    return UserState.activeShop?.id ?? 0;
  };

  /**
   * Submit a WhatsApp template for validation
   * @param templateId - The template ID to submit
   * @param data - The operator data
   * @returns The validation result with updated status
   */
  const submitTemplate = async (
    templateId: number,
    data: any,
  ): Promise<WhatsappValidationResult> => {
    const shopId = getShopId();
    if (!shopId || !templateId) {
      return {
        success: false,
        status: WhatsappValidationStatus.ERROR,
        error: 'Missing shop ID or template ID',
      };
    }

    isSubmitting.value = true;

    try {
      const response = await SubmitWhatsappValidation(shopId, { template_display_selector_id: templateId });

      if (response.success && response.data && response.data.template) {
        const { status } = response.data.template;

        const mappedStatus = (WhatsappValidationStatus as any)[status] || WhatsappValidationStatus.PENDING;
        return { success: true, status: mappedStatus };
      }

      return {
        success: false,
        status: WhatsappValidationStatus.ERROR,
        error: response.error || 'Unknown error',
      };
    } catch (error: any) {
      return {
        success: false,
        status: WhatsappValidationStatus.ERROR,
        error: error.message || 'Submission failed',
      };
    } finally {
      isSubmitting.value = false;
    }
  };

  /**
   * Check the current validation status of a template
   * @param templateId - The template ID to check
   * @returns The current validation status
   */
  const checkStatus = async (templateId: string | number): Promise<WhatsappValidationStatus> => {
    const shopId = getShopId();
    if (!shopId || !templateId) {
      return WhatsappValidationStatus.ERROR;
    }

    isChecking.value = true;

    try {
      const response = await GetWhatsappValidationStatus(shopId, templateId.toString());

      if (response.success && response.template) {
        const { status } = response.template;

        return (WhatsappValidationStatus as any)[status] || WhatsappValidationStatus.PENDING;
      }

      return WhatsappValidationStatus.ERROR;
    } catch (error) {
      return WhatsappValidationStatus.ERROR;
    } finally {
      isChecking.value = false;
    }
  };

  /**
   * Start polling for status updates
   * @param templateId - The template ID to poll
   * @param onStatusChange - Callback when status changes
   * @param intervalMs - Polling interval in milliseconds (default: 5000)
   */
  const startPolling = (
    templateId: string | number,
    onStatusChange: (status: WhatsappValidationStatus) => void,
    intervalMs = 5000,
  ) => {
    // Stop any existing polling
    stopPolling();

    pollingIntervalId.value = window.setInterval(async () => {
      const status = await checkStatus(templateId);
      onStatusChange(status);

      // Stop polling if status is no longer pending (PENDING or IN_APPEAL)
      if (!isPendingStatus(status)) {
        stopPolling();
      }
    }, intervalMs) as unknown as number;
  };

  /**
   * Stop polling
   */
  const stopPolling = () => {
    if (pollingIntervalId.value) {
      window.clearInterval(pollingIntervalId.value);
      pollingIntervalId.value = null;
    }
  };

  return {
    isSubmitting,
    isChecking,
    submitTemplate,
    checkStatus,
    startPolling,
    stopPolling,
    getShopId,
  };
}

/**
 * Centralized polling manager for multiple WhatsApp operators
 * Used in FlowRenderer to manage polling for all PENDING operators
 */
export function useWhatsappPollingManager() {
  const activePollers = ref<Map<string, number>>(new Map());
  const { checkStatus } = useWhatsappTemplateValidation();

  /**
   * Start polling for a specific step
   * @param stepId - The step ID
   * @param templateId - The template ID
   * @param onStatusChange - Callback when status changes
   */
  const startPollingForStep = (
    stepId: string,
    templateId: string | number,
    onStatusChange: (stepId: string, status: WhatsappValidationStatus) => void,
  ) => {
    // Stop existing polling for this step
    stopPollingForStep(stepId);

    const intervalId = window.setInterval(async () => {
      const status = await checkStatus(templateId);
      onStatusChange(stepId, status);

      // Stop polling if status is no longer pending (PENDING or IN_APPEAL)
      if (!isPendingStatus(status)) {
        stopPollingForStep(stepId);
      }
    }, 5000) as unknown as number;

    activePollers.value.set(stepId, intervalId);
  };

  /**
   * Stop polling for a specific step
   */
  const stopPollingForStep = (stepId: string) => {
    const intervalId = activePollers.value.get(stepId);
    if (intervalId) {
      window.clearInterval(intervalId);
      activePollers.value.delete(stepId);
    }
  };

  /**
   * Update polling based on current flow state
   * Starts polling for PENDING steps, stops for others
   * Also performs an immediate check for new PENDING steps
   */
  const updatePolling = async (
    steps: Array<{ id: string; type: string; parameters: any }>,
    onStatusChange: (stepId: string, status: WhatsappValidationStatus) => void,
  ) => {
    const pendingStepIds = new Set<string>();

    // Find all WhatsApp steps with PENDING status
    for (const step of steps) {
      if (step.type === 'boxsendwhatsapp') {
        const status = step.parameters?.template_validation_status;
        const templateId = step.parameters?.template_display_selector_id;

        if (isPendingStatus(status) && templateId) {
          pendingStepIds.add(step.id);

          // Start polling if not already polling
          if (!activePollers.value.has(step.id)) {
            // Perform immediate check before starting polling
            const currentStatus = await checkStatus(templateId);
            onStatusChange(step.id, currentStatus);

            // Only start polling if still pending after immediate check
            if (isPendingStatus(currentStatus)) {
              startPollingForStep(step.id, templateId, onStatusChange);
            }
          }
        }
      }
    }

    // Stop polling for steps that are no longer pending
    for (const stepId of activePollers.value.keys()) {
      if (!pendingStepIds.has(stepId)) {
        stopPollingForStep(stepId);
      }
    }
  };

  /**
   * Stop all polling
   */
  const stopAllPolling = () => {
    for (const stepId of activePollers.value.keys()) {
      stopPollingForStep(stepId);
    }
  };

  return {
    activePollers,
    startPollingForStep,
    stopPollingForStep,
    updatePolling,
    stopAllPolling,
  };
}
